/**
 * Shared utility functions used by grid modules (news-events-grid, reference-grid-search).
 *
 * All functions are pure and side-effect-free — no DOM access, no module state.
 * Import only what each module needs to keep the dependency graph explicit.
 *
 * @module grid-utils
 */

/**
 * @typedef {Object} ImageUrls
 * @property {string} jpg - JPEG image URL.
 * @property {(string|null)} webp - WebP image URL, or null if unavailable.
 */

/**
 * Returns the first non-null, non-undefined value from a list of candidate keys.
 *
 * @param {Record<string, any>} source - Source object to inspect.
 * @param {string[]} keys - Candidate keys in priority order.
 * @returns {any}
 */
export function pick(source, keys) {
  const resolvedKey = keys.find((key) => source?.[key] !== undefined && source?.[key] !== null);
  return resolvedKey ? source[resolvedKey] : undefined;
}

/**
 * Escapes HTML special characters to prevent XSS in template strings.
 *
 * @param {string} value - Raw text value.
 * @returns {string}
 */
export function escapeHtml(value = '') {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll('\'', '&#39;');
}

/**
 * Replaces `{key}` placeholder tokens in a localized string template.
 *
 * @param {string} template - String template (e.g. '{shown} of {total} results loaded.').
 * @param {Record<string, string | number>} values - Replacement values keyed by placeholder name.
 * @returns {string}
 */
export function formatMessage(template, values = {}) {
  return Object.entries(values).reduce((message, [key, value]) => {
    return message.replaceAll(`{${key}}`, String(value));
  }, template);
}

/**
 * Returns a normalized language code for the stage API endpoint.
 *
 * Prefers the `data-stage-language` attribute on the component root,
 * falling back to the document `lang` attribute (resolves to 'fi' or 'en').
 *
 * @param {HTMLElement} root - Component root element.
 * @returns {string}
 */
export function getStageLanguage(root) {
  const explicitLanguage = root.dataset.stageLanguage?.trim();
  if (explicitLanguage) return explicitLanguage;

  const documentLanguage = document.documentElement.lang?.toLowerCase();
  return documentLanguage?.startsWith('fi') ? 'fi' : 'en';
}

/**
 * Attempts to resolve image URLs from a raw CMS item.
 *
 * Handles:
 * - structured image objects with 'card' variant (returns { jpg, webp })
 * - flat string values on common image keys (returns { jpg, webp: null })
 * - nested image objects (e.g. { jpg, url, src, … })
 * - array-valued 'images' / 'media' fields (uses first entry)
 *
 * @param {Record<string, any>} item - Raw API item.
 * @returns {ImageUrls}
 */
export function getImageUrl(item) {
  const variantContainer = pick(item, ['images', 'media']);

  // Prefer explicit card conversion when available, fall back through card-large → defaultImage → medium.
  if (variantContainer && typeof variantContainer === 'object' && !Array.isArray(variantContainer)) {
    const cardVariant = variantContainer.card || variantContainer['card-large'];

    if (cardVariant && typeof cardVariant === 'object') {
      return {
        jpg: cardVariant.jpg || '',
        webp: cardVariant.webp || null
      };
    }

    const defaultImage = variantContainer.defaultImage;
    if (defaultImage && typeof defaultImage === 'object' && defaultImage.jpg) {
      return {
        jpg: defaultImage.jpg,
        webp: defaultImage.webp || null
      };
    }

    const variantJpg = pick(variantContainer, ['jpg', 'url', 'src', 'medium', 'large', 'full']) || '';
    if (variantJpg) {
      return {
        jpg: variantJpg,
        webp: variantContainer.webp || null
      };
    }
  }

  const directImage = pick(item, [
    'image',
    'imageUrl',
    'image_url',
    'thumbnail',
    'thumbnailUrl',
    'thumbnail_url',
    'featuredImage',
    'featured_image'
  ]);

  // If it's a structured object with 'card' variant (new format), use it.
  if (directImage && typeof directImage === 'object' && directImage.card) {
    return {
      jpg: directImage.card.jpg || '',
      webp: directImage.card.webp || null
    };
  }

  // If it's a plain string, wrap it as jpg with no webp.
  if (typeof directImage === 'string') {
    return {
      jpg: directImage,
      webp: null
    };
  }

  // If it's an object with jpg/url/src fields, extract the jpg.
  if (directImage && typeof directImage === 'object') {
    const jpgUrl = pick(directImage, ['jpg', 'url', 'src', 'medium', 'large', 'full']) || '';
    return {
      jpg: jpgUrl,
      webp: directImage.webp || null
    };
  }

  // Some CMS responses group images under an array field.
  const images = variantContainer;

  if (Array.isArray(images) && images.length) {
    const firstImage = images[0];

    if (typeof firstImage === 'string') {
      return {
        jpg: firstImage,
        webp: null
      };
    }

    if (firstImage && typeof firstImage === 'object') {
      // Check if it has 'card' variant.
      if (firstImage.card) {
        return {
          jpg: firstImage.card.jpg || '',
          webp: firstImage.card.webp || null
        };
      }

      const jpgUrl = pick(firstImage, ['jpg', 'url', 'src', 'medium', 'large', 'full']) || '';
      return {
        jpg: jpgUrl,
        webp: firstImage.webp || null
      };
    }
  }

  return {
    jpg: '',
    webp: null
  };
}

/**
 * Resolves one item URL into a plain href string.
 *
 * The backend may return either:
 * - a direct URL string
 * - an object like `{ url, target }`
 *
 * @param {Record<string, any>} item - Raw API item.
 * @returns {string}
 */
export function resolveItemUrl(item) {
  const rawUrl = pick(item, ['url', 'link', 'permalink']);

  if (typeof rawUrl === 'string' && rawUrl.trim()) {
    return rawUrl;
  }

  if (rawUrl && typeof rawUrl === 'object') {
    const nestedUrl = pick(rawUrl, ['url', 'href']);
    if (typeof nestedUrl === 'string' && nestedUrl.trim()) {
      return nestedUrl;
    }
  }

  return '#';
}

/**
 * Resolves the CTA "read more" text with strict priority:
 * 1. Server item value (`read_more_text` / `readMoreText`), if non-empty and valid.
 * 2. Translations fallback (`translations.readMore`).
 *
 * Backward compatibility: server values of exactly `"Read"` (case-insensitive)
 * are treated as a legacy truncation and normalized to the translations fallback.
 *
 * @param {Record<string, any>} item - Raw API item.
 * @param {{ readMore: string }} translations - Object with at least a `readMore` property.
 * @returns {string}
 */
export function resolveReadMoreText(item, translations) {
  const serverValue = pick(item, ['read_more_text', 'readMoreText']);

  if (typeof serverValue === 'string' && serverValue.trim()) {
    const normalizedServerValue = serverValue.trim();

    // Treat English default values as unset — fall through to localized translations.
    const lower = normalizedServerValue.toLowerCase();
    if (lower === 'read' || lower === 'read more') {
      return translations.readMore;
    }

    return normalizedServerValue;
  }

  return translations.readMore;
}
